import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTimes, FaMapMarkerAlt, FaCity, FaHome } from 'react-icons/fa';
import { trackEvent } from '../utils/analytics';
import { fetchVillageJson } from '../utils/villageData';

const GlobalSearch = ({ className }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [index, setIndex] = useState([]);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    // Load index only once when opened
    useEffect(() => {
        if (isOpen && index.length === 0) {
            setLoading(true);
            fetchVillageJson('search_index.json')
                .then(data => {
                    setIndex(data);
                    setLoading(false);
                })
                .catch(err => {
                    console.error("Failed to load search index", err);
                    setLoading(false);
                });
        }
    }, [isOpen, index.length]);

    // Focus input on open
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current.focus(), 100);
        }
    }, [isOpen]);

    // Handle Search Filtering
    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            return;
        }

        if (index.length > 0) {
            const lowerQ = query.toLowerCase();
            // Simple startsWith + includes logic
            // Prioritize startsWith
            const filtered = index
                .filter(item => item.searchStr.includes(lowerQ))
                .sort((a, b) => {
                    const aStarts = a.searchStr.startsWith(lowerQ);
                    const bStarts = b.searchStr.startsWith(lowerQ);
                    if (aStarts && !bStarts) return -1;
                    if (!aStarts && bStarts) return 1;
                    return 0;
                })
                .slice(0, 10); // Limit to 10 results

            setResults(filtered);
        }
    }, [query, index]);

    const handleSelect = (item) => {
        trackEvent('global_search_select', {
            type: item.type,
            label: item.label
        });
        if (item.type === 'state') {
            navigate(`/${item.slug}`);
        } else if (item.type === 'subdistrict') {
            // /state/district/subdistrict
            navigate(`/${item.stateSlug}/${item.distSlug}/${item.slug}`);
        } else if (item.type === 'village') {
            navigate(`/${item.stateSlug}/${item.distSlug}/${item.subdistrictSlug}/${item.slug}`);
        } else {
            // district
            navigate(`/${item.stateSlug}/${item.slug}`);
        }
        closeSearch();
    };

    const closeSearch = () => {
        setIsOpen(false);
        setQuery('');
    };

    const getIcon = (type) => {
        switch (type) {
            case 'state': return <FaMapMarkerAlt className="text-success" />;
            case 'district': return <FaCity className="text-primary" />;
            case 'subdistrict': return <FaCity className="text-info opacity-75" />;
            case 'village': return <FaHome className="text-warning" />;
            default: return <FaSearch />;
        }
    }

    return (
        <>
            <button
                className={`btn btn-light rounded-circle shadow-sm d-flex align-items-center justify-content-center ${className || ''}`}
                style={{ width: '40px', height: '40px' }}
                onClick={() => setIsOpen(true)}
                aria-label="Search"
            >
                <FaSearch className="text-primary" />
            </button>

            {isOpen && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 bg-white"
                    style={{ zIndex: 1100, overflowY: 'auto' }}
                >
                    <div className="container py-3">
                        {/* Header */}
                        <div className="d-flex align-items-center gap-3 mb-4">
                            <FaSearch className="text-muted fs-5" />
                            <input
                                ref={inputRef}
                                type="text"
                                className="form-control border-0 fs-4 shadow-none px-0"
                                placeholder="Search State, District or Taluka..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                            <button className="btn p-0" onClick={closeSearch}>
                                <FaTimes className="fs-3 text-secondary" />
                            </button>
                        </div>

                        {/* Loading */}
                        {loading && <div className="text-muted">Loading search index...</div>}

                        {/* Results */}
                        <div className="list-group list-group-flush">
                            {results.map((item, idx) => (
                                <button
                                    key={idx}
                                    className="list-group-item list-group-item-action border-0 px-0 py-3 d-flex align-items-center"
                                    style={{ transition: 'background 0.2s' }}
                                    onClick={() => handleSelect(item)}
                                >
                                    <div className="bg-light p-2 rounded-circle me-3" style={{ minWidth: '40px', textAlign: 'center' }}>
                                        {getIcon(item.type)}
                                    </div>
                                    <div>
                                        <div className="fw-medium text-dark h5 mb-0">{item.label}</div>
                                        <small className="text-muted text-capitalize">
                                            {item.type === 'subdistrict' ? 'Taluka / Subdistrict' : item.type === 'village' ? 'Village' : item.type}
                                        </small>
                                    </div>
                                </button>
                            ))}

                            {query && results.length === 0 && !loading && (
                                <div className="text-muted mt-3">No matching locations found.</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default GlobalSearch;
